import { Button, Modal, Space, Typography } from 'antd';
import { memo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { uiActions } from '../../store/slices/ui';
import { yourLibraryActions } from '../../store/slices/yourLibrary';
import { useAppDispatch, useAppSelector } from '../../store/store';

export const ImportOnboardingModal = memo(() => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const open = useAppSelector((state) => state.ui.importOnboardingOpen);
  const importStatus = useAppSelector((state) => state.yourLibrary.importStatus ?? 'idle');
  const importError = useAppSelector((state) => state.yourLibrary.importError ?? null);

  const closeModal = useCallback(() => {
    dispatch(uiActions.closeImportOnboarding());
  }, [dispatch]);

  useEffect(() => {
    if (open && location.pathname === '/settings/library-sync') {
      dispatch(uiActions.closeImportOnboarding());
    }
  }, [dispatch, location.pathname, open]);

  const navigateToSettings = useCallback(() => {
    navigate('/settings/library-sync', { state: { fromOnboarding: true } });
  }, [navigate]);

  const handleStartImport = useCallback(() => {
    dispatch(yourLibraryActions.importLibraryFromSpotify());
    navigateToSettings();
  }, [dispatch, navigateToSettings]);

  if (!open) return null;

  const isWorking = importStatus === 'pending';

  return (
    <Modal
      width={560}
      style={{ maxWidth: 580, top: '31vh' }}
      open={open}
      footer={null}
      onCancel={closeModal}
      destroyOnClose
      maskClosable={!isWorking}
      className='import-onboarding-modal'
      wrapClassName='overlay-modal'
    >
      <Space direction='vertical' size={24} style={{ display: 'flex' }}>
        <div className='import-onboarding-modal__text'>
          <Typography.Title
            level={4}
            style={{
              margin: 0,
              color: '#fff',
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: -0.2,
              marginBottom: 18,
              textAlign: 'center',
            }}
          >
            Set up your library
          </Typography.Title>
          <Typography.Paragraph
            style={{
              marginTop: 0,
              marginBottom: 6,
              color: 'rgba(255, 255, 255, 0.82)',
              fontSize: 16,
              lineHeight: 1.5,
              textAlign: 'center',
            }}
          >
            <span style={{ display: 'block', marginBottom: 6 }}>
              This app is built around continuous listening rather than playlists — think of it as a classical radio station that you shape.
            </span>
            <span style={{ display: 'block', marginBottom: 6 }}>
              Importing your library helps the app understand what you already know, so it can balance familiar listening and discovery from the start.
            </span>
            <Typography.Link
              href='/how-it-works'
              onClick={(event) => {
                event.preventDefault();
                closeModal();
                navigate('/how-it-works');
              }}
              style={{
                display: 'inline-block',
                color: 'rgba(255, 255, 255, 0.68)',
                marginTop: 2,
              }}
            >
              Learn more about how it works →
            </Typography.Link>
          </Typography.Paragraph>
        </div>

        {importError ? (
          <Typography.Text type='danger' style={{ display: 'block' }}>
            {importError}
          </Typography.Text>
        ) : null}

        <div className='import-onboarding-modal__cta'>
          <Space size={10} direction='vertical' style={{ width: '100%' }}>
            <Button
              type='primary'
              size='large'
              onClick={handleStartImport}
              loading={isWorking}
              disabled={isWorking}
              block
              style={{
                fontWeight: 700,
                height: 48,
                minWidth: 240,
                borderRadius: 8,
                fontSize: 16,
                background: '#3b82f6',
                borderColor: '#3b82f6',
              }}
            >
              Import from Spotify
            </Button>
            <Typography.Paragraph
              style={{
                marginTop: 4,
                marginBottom: 0,
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: 13,
                lineHeight: 1.3,
                textAlign: 'center',
              }}
            >
              This won’t change anything in your Spotify account.
            </Typography.Paragraph>
          </Space>
        </div>
      </Space>
    </Modal>
  );
});

ImportOnboardingModal.displayName = 'ImportOnboardingModal';
